import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AD_FORMAT_QUERY_KEYS } from '@/entities/ad-format';
import {
  createFormat,
  createGroup,
  getFormatHistory,
  getLibrary,
  restoreFormat,
  updateFormat,
} from '../api/format-specs-admin.api';
import { FORMAT_SPECS_ADMIN_KEYS } from '../api/query-keys';
import type { FormatPatch, NewFormatInput, NewGroupInput } from './types';

export function useFormatLibrary() {
  return useQuery({
    queryKey: FORMAT_SPECS_ADMIN_KEYS.library(),
    queryFn: ({ signal }) => getLibrary(signal),
  });
}

/**
 * Every save is a new library version, so the editor and the Content Studio pickers both read
 * stale data until refetched.
 */
function useRefreshAfterSave() {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: FORMAT_SPECS_ADMIN_KEYS.all }),
      queryClient.invalidateQueries({ queryKey: AD_FORMAT_QUERY_KEYS.formatSpecs() }),
    ]);
  };
}

export function useUpdateFormat() {
  const refresh = useRefreshAfterSave();
  return useMutation({
    mutationFn: ({ key, patch }: { key: string; patch: FormatPatch }) => updateFormat(key, patch),
    onSuccess: refresh,
  });
}

export function useCreateFormat() {
  const refresh = useRefreshAfterSave();
  return useMutation({
    mutationFn: (input: NewFormatInput) => createFormat(input),
    onSuccess: refresh,
  });
}

export function useCreateGroup() {
  const refresh = useRefreshAfterSave();
  return useMutation({
    mutationFn: (input: NewGroupInput) => createGroup(input),
    onSuccess: refresh,
  });
}

export function useFormatHistory(formatKey: string | null) {
  return useQuery({
    queryKey: FORMAT_SPECS_ADMIN_KEYS.history(formatKey ?? ''),
    queryFn: ({ signal }) => getFormatHistory(formatKey ?? '', signal),
    enabled: formatKey !== null,
  });
}

export function useRestoreFormat() {
  const refresh = useRefreshAfterSave();
  return useMutation({
    mutationFn: ({
      version,
      formatKey,
      confirmBreaking,
    }: {
      version: number;
      formatKey: string;
      confirmBreaking: boolean;
    }) => restoreFormat(version, formatKey, confirmBreaking),
    onSuccess: refresh,
  });
}
